import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";

import { AuthProvider, useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import VoiceAgent from "@/components/VoiceAgent";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Software from "@/pages/Software";
import PlantEngineering from "@/pages/PlantEngineering";
import Products from "@/pages/Products";
import Blogs from "@/pages/Blogs";
import Contact from "@/pages/Contact";
import SchoolManagementSystem from "@/pages/SchoolManagementSystem";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Piping from "@/pages/Piping";
import CivilStructural from "@/pages/CivilStructural";
import ElectricalInstrumentation from "@/pages/ElectricalInstrumentation";
import Mechanical from "@/pages/Mechanical";
import Materials from "@/pages/Materials";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";

function ScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [hash, pathname]);
  return null;
}

function Protected({ children, role }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-[#94A3B8]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <ScrollToHash />
      <main className="flex-1">{children}</main>
      <Footer />
      <ChatWidget />
      <VoiceAgent />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/software" element={<Software />} />
            <Route path="/plant-engineering" element={<PlantEngineering />} />
            <Route path="/piping" element={<Piping />} />
            <Route path="/civil-structural" element={<CivilStructural />} />
            <Route path="/electrical-instrumentation" element={<ElectricalInstrumentation />} />
            <Route path="/mechanical" element={<Mechanical />} />
            <Route path="/materials" element={<Materials />} />
            <Route path="/products" element={<Products />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:slug" element={<Blogs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/products/school-management-system" element={<SchoolManagementSystem />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Protected><CustomerDashboard /></Protected>} />
            <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </AuthProvider>
  );
}
